import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid, QuickFilter, QuickFilterControl } from "@mui/x-data-grid";
import { useGridApiRef } from "@mui/x-data-grid";
import { Tag } from "primereact/tag";
import { Col, Row } from "react-bootstrap";
import moment from "moment";
import Langs, { Localize } from "../../app/lang/langs";
import { getEmploymentPromises } from "../../components/employmentPromises/employmentPromisesSlice";
import ImageButton from "../../components/buttons/ImageButton";
import addIcon from "../../assets/images/add.png";
import addIconFocus from "../../assets/images/addFocus.png";
import EmploymentPromisesAddModal from "./EmploymentPromisesAddModal";
import EmploymentPromisesEditModal from "./EmploymentPromisesEditModal";
import styles from "./CollabsView.module.css";

export default function EmploymentPromisesView() {
  const dispatch = useDispatch();
  const mounted = useRef();
  const apiRef = useGridApiRef();

  const list = useSelector((state) => state.employmentPromises.list);
  const isMobile = useSelector((state) => state.settings.isMobile);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [onViewPromise, setOnViewPromise] = useState(null);
  const [onViewVisibile, setOnViewVisible] = useState(false);
  const [onAddViewVisible, setOnAddViewVisible] = useState(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      dispatch(getEmploymentPromises());
    }
  });

  const getFormattedDate = (date) => {
    if (!date) return null;
    return moment(date).format("DD/MM/YYYY");
  };

  const columns = useMemo(() => [
    { field: "id", headerName: Localize("ID"), minWidth: 112, sortable: true, filterable: true },
    { field: "company_name", headerName: Localize("Company"), minWidth: 128, sortable: true, filterable: true },
    { field: "family_name", headerName: Localize("Family Name"), minWidth: 128, sortable: true, filterable: true },
    { field: "name", headerName: Localize("Name"), minWidth: 144, sortable: true, filterable: true },
    { field: "email", headerName: Localize("EMAIL"), minWidth: 144, sortable: true, filterable: true },
    {
      field: "progression", headerName: Localize("Progression"), minWidth: 160, sortable: true, filterable: true,
      renderCell: (params) => {
        const status = params.value;
        let severity;
        switch (status) {
          case "Attente d'envoi": severity = "warning"; break;
          case "Envoyé": severity = "info"; break;
          case "Reçu": severity = "success"; break;
          default: severity = null;
        }
        return <Tag value={status} severity={severity} />;
      },
    },
    {
      field: "status", headerName: Localize("STATUS"), minWidth: 128, sortable: true, filterable: true,
      renderCell: (params) => (
        <span className={`badge ${params.value === "OK" ? "bg-success" : "bg-danger"}`}>
          {params.value}
        </span>
      ),
    },
    {
      field: "request_date", headerName: Localize("REQUEST_DATE"), minWidth: 144,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => params.value ? <label>{getFormattedDate(params.value)}</label> : null,
    },
    {
      field: "sent_date", headerName: Localize("SENT_DATE"), minWidth: 144,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => params.value ? <label>{getFormattedDate(params.value)}</label> : null,
    },
    {
      field: "start_date", headerName: Localize("START_DATE"), minWidth: 144,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => params.value ? <label>{getFormattedDate(params.value)}</label> : null,
    },
  ], []);

  const onRowDoubleClick = (params) => {
    setOnViewVisible(true);
    setOnViewPromise(params.row);
  };

  let scrollHeight = "calc(100vh - 160px)";
  switch (isMobile) {
    case 2: scrollHeight = "calc(100vh - 220px)"; break;
    case 3: scrollHeight = "calc(100vh - 160px)"; break;
    default: break;
  }

  const CustomToolbar = () => (
    <Row className="p-2">
      <Col lg={6}>
        <QuickFilter debounceMs={300}>
          <QuickFilterControl />
        </QuickFilter>
      </Col>
      <Col md={6} className="d-flex justify-content-end">
        <ImageButton
          image={addIcon}
          imageFocus={addIconFocus}
          onClick={() => setOnAddViewVisible(true)}
          active={true}
          width={32}
        />
      </Col>
    </Row>
  );

  return (
    <div className={styles.collabsCard}>
      <div style={{ height: scrollHeight, width: "100%" }}>
        <DataGrid
          apiRef={apiRef}
          rows={list || []}
          columns={columns}
          getRowId={(row) => row.id}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          onRowDoubleClick={onRowDoubleClick}
          slots={{ toolbar: CustomToolbar }}
          rowHeight={52}
          disableRowSelectionOnClick
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
            "& .MuiDataGrid-row": { cursor: "pointer" },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e0e0e0",
              minHeight: 52,
            },
            "& .MuiTablePagination-root": {
              width: "100%",
              overflow: "hidden",
            },
            "& .MuiTablePagination-toolbar": {
              justifyContent: "flex-end",
              alignItems: "center",
              minHeight: 52,
              px: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              margin: 0,
            },
          }}
        />
      </div>
      <EmploymentPromisesAddModal
        visible={onAddViewVisible}
        setVisible={setOnAddViewVisible}
      />
      <EmploymentPromisesEditModal
        visible={onViewVisibile}
        setVisible={setOnViewVisible}
        onViewItem={onViewPromise}
        setOnViewItem={setOnViewPromise}
      />
    </div>
  );
}
